import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { LIMIT } from "../../../constants/fiasAPI";
import { AsynchronousTagProps, ExtTagData } from "../../../types/common";
import { TagData } from "../../../types/api/tag";
import { createNewTag, getTags } from "../../../api/tagAPI";
import useDebounce from "../../../utils/useDebounce";
import { docTagIdsObject } from "../../../pages/DocumentsPage/types/documentTypes";

interface Props extends Omit<AsynchronousTagProps, "onChange"> {
    onChange: (value: string) => void;
}

const filter = createFilterOptions<ExtTagData>();

const AsynchronousTags: React.FC<Props> = ({
    onChange,
    value,
    type,
    label = "Тэг",
    callbackResult,
    isDocTagsOnly,
    enableAdd,
}) => {
    const [options, setOptions] = useState<Array<ExtTagData>>();
    const [select, setSelect] = useState<ExtTagData>();
    const date = new Date();
    const [query, setQuery] = useState("");
    const debQuery = useDebounce(query, 400);
    const [load, setLoad] = useState(false);
    const [error, setError] = useState<{ query: string; message: string }>({ query: "", message: "" });
    const fetchData = () => {
        setLoad(true);
        getTags(
            result => {
                if (isDocTagsOnly) {
                    const mappedData = result.data.reduce(
                        (acc, el) => (docTagIdsObject[el.id as number] ? [...acc, el] : acc),
                        [] as TagData[],
                    ) as ExtTagData[];
                    setOptions(mappedData);
                } else {
                    setOptions(result.data as ExtTagData[]);
                }

                setLoad(false);
            },
            () => {
                setLoad(false);
            },
            {
                ...(query && { q: query }),
                type,
                [LIMIT]: -1,
            },
        );
    };

    useEffect(() => {
        fetchData();
    }, [debQuery]);

    useEffect(() => {
        setQuery(value);
    }, []);

    useEffect(() => {
        if (error.message && query !== error.query) {
            setError({ query: "", message: "" });
        }
    }, [query]);

    const valueGet = (newValue: string) => ({
        name: newValue,
    });

    return (
        <Autocomplete
            value={select}
            // @ts-expect-error fix later
            onChange={async (_event, newValue: string | ExtTagData | string) => {
                if (typeof newValue === "string") {
                    const value = valueGet(newValue);
                    setSelect(value);
                    callbackResult && callbackResult(value);
                    onChange(value.name);
                } else if (newValue?.inputValue) {
                    let newItem: TagData | null = null;
                    await createNewTag(
                        { name: newValue?.inputValue, type },
                        resp => {
                            newItem = resp.data;
                        },
                        err => {
                            setError({ query: newValue?.inputValue ?? "", message: err.errors.name[0] });
                        },
                    );
                    if (newItem) {
                        setSelect(newItem);
                        callbackResult && callbackResult(newItem);
                        onChange((newItem ? newItem : { name: "" }).name);
                    }
                } else if (newValue) {
                    setSelect(newValue);
                    callbackResult && callbackResult(newValue);
                    onChange(newValue?.name ?? "");
                }
            }}
            inputValue={query ?? ""}
            onInputChange={(_event, newInputValue) => {
                setQuery(newInputValue);
            }}
            noOptionsText={"Нет совпадений"}
            getOptionLabel={option => {
                if (typeof option === "string") return option;
                if (option.inputValue) return option.inputValue;
                return option.name ?? "";
            }}
            // getOptionLabel={option => (typeof option === "string" ? option : option.name)}
            options={options || []}
            loading={load}
            freeSolo
            selectOnFocus
            clearOnBlur
            filterOptions={(options, params) => {
                const mappedOptions = options.map(el => (typeof el === "string" ? { name: el } : el));

                const filtered = filter(mappedOptions, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = mappedOptions.some(option => inputValue === option?.name);
                if (enableAdd && inputValue !== "" && !isExisting) {
                    filtered.push({
                        inputValue,
                        name: `Добавить "${inputValue}"`,
                    });
                }

                return filtered;
            }}
            renderOption={(props, option) => {
                return (
                    <li {...props} key={typeof option === "string" ? option : option.id}>
                        <Typography gutterBottom>{typeof option === "string" ? option : option.name ?? ""}</Typography>
                    </li>
                );
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    size={"small"}
                    label={label}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {load ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    error={!!error.message}
                    helperText={error.message}
                />
            )}
        />
    );
};
export default AsynchronousTags;
